import { presetSelectionAtom, sourceSelectionModeAtom } from '@/atoms/sourceControlAtoms';
import { getRegion } from '@/helpers/getRegion';
import { useAuthToken } from '@/hooks/useAuthToken';
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import { useSessionStage, type SessionStage } from '@/hooks/useSessionStage';
import {
  trackChatCreated as trackChatCreatedHost,
  trackChatDeleted as trackChatDeletedHost,
  trackChatViewed as trackChatViewedHost,
} from '@/lib/analytics/scribe/chat';
import {
  trackEvidenceChatAborted,
  trackEvidenceChatCompleted,
  trackEvidenceChatError,
  trackEvidenceChatSidebarClose,
  trackEvidenceChatSidebarOpen,
  trackEvidenceChatSubmitted,
  trackEvidenceClickPastSessionClicked,
  trackEvidenceDeepLinkSignInClicked,
  trackEvidenceDeepLinkSignUpClicked,
  trackEvidenceDeleteChatClicked,
  trackEvidenceJurisdictionChanged,
  trackEvidenceLanguageChanged,
  trackEvidenceNewChatClicked,
  trackEvidenceProTrialButtonStarted,
  trackEvidenceSaveChatClicked,
  trackEvidenceSidebarClicked,
  trackEvidenceSourcesListClicked,
  trackEvidenceSourcesListCustomClicked,
  trackEvidenceSourcesListLocalGuidelinesClicked,
  trackEvidenceSourcesListPresetClicked,
  trackEvidenceSourcesListWebSearchClicked,
  trackEvidenceSpecialtyChanged,
  trackEvidenceToastShown,
  trackEvidenceToastViewClicked,
  trackEvidenceUnsaveChatClicked,
} from '@/lib/analytics/scribe/evidence';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  normalizeQueueDurationMs,
  type ChipOrigin,
  type ChipSurface,
  type MessageEntryMethod,
} from '@/models/analytics/features/chat';
import {
  EvidenceLocation,
  type EvidenceChatAttachmentReason,
  type EvidenceChatBlockType,
  type EvidenceChatErrorType,
  type EvidenceCopyConfirmationAction,
  type EvidenceCopyConfirmationSurface,
  type EvidenceShareSocialPlatform,
  type EvidenceSourceSelectionMode,
} from '@/models/analytics/features/evidence';
import { useInvokeOnce } from '@heidi/utils';
import {
  useCombinedQueriesTeamGeneralInfo,
  useCurrentWorkspace,
} from '@heidi/workspace/data';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

/**
 * The `message_submitted` / `message_received` Iglu schemas (and the retired
 * `evidence_chat_*` dual-writes) constrain `origin` / `session_stage` to
 * enums, several counts to `integer`, and `sources` / `tools_used` to string
 * arrays. A value that violates any of these sends the whole event to Snowplow
 * failed events (silent analytics loss). These sanitizers clamp the
 * constrained fields at the tracking boundary so a stray value degrades to
 * `undefined` instead of poisoning the event.
 */
const CHIP_ORIGIN_VALUES: readonly ChipOrigin[] = ['default', 'library', 'custom'];
const SESSION_STAGE_VALUES: readonly SessionStage[] = ['pre', 'during', 'post'];

const toChipOrigin = (value: unknown): ChipOrigin | undefined =>
  typeof value === 'string' && (CHIP_ORIGIN_VALUES as readonly string[]).includes(value)
    ? (value as ChipOrigin)
    : undefined;

const toSessionStage = (value: unknown): SessionStage | undefined =>
  typeof value === 'string' && (SESSION_STAGE_VALUES as readonly string[]).includes(value)
    ? (value as SessionStage)
    : undefined;

const toInteger = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : undefined;

const toRequiredInteger = (value: unknown): number => toInteger(value) ?? 0;

const toStringArray = (value: unknown): string[] | undefined =>
  Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : undefined;

/**
 * Hook that provides Evidence analytics tracking utilities.
 * Handles location detection, user context, and dispatch methods.
 */
export const useEvidenceAnalytics = () => {
  const router = useRouter();
  const token = useAuthToken();
  const { hasEvidencePro } = useEvidenceBillingInfo();
  const { hasTeam } = useCombinedQueriesTeamGeneralInfo();
  const isManaged = useCurrentWorkspace()?.is_managed;
  const sourceSelectionMode = useAtomValue(sourceSelectionModeAtom);
  const presetSelection = useAtomValue(presetSelectionAtom);
  const sessionStage = useSessionStage();

  // Determine location based on route
  const evidenceLocation = useMemo((): EvidenceLocation => {
    const pathname = router.pathname;

    // Check if user is not authenticated (logged out)
    if (!token) {
      return EvidenceLocation.LoggedOut;
    }

    // /scribe/session - in session
    if (pathname.includes('/scribe/session')) {
      return EvidenceLocation.InSession;
    }

    // /scribe/chat - out of session (logged in but not in a session)
    if (pathname.includes('/scribe/chat')) {
      return EvidenceLocation.OutOfSession;
    }

    // /chat - logged out (public chat)
    if (pathname.includes('/chat') && !pathname.includes('/scribe')) {
      return EvidenceLocation.LoggedOut;
    }

    // Default to out of session for other authenticated routes
    return EvidenceLocation.OutOfSession;
  }, [router.pathname, token]);

  // Get user region
  const region = useMemo(() => getRegion(), []);

  // Check if user is on a team
  const isTeamMember = hasTeam;

  // Base props that should be included in most events
  const baseProps = useMemo(
    () => ({
      evidence_location: evidenceLocation,
      region,
      is_evidence_pro: hasEvidencePro,
      is_team_member: isTeamMember,
      is_managed: isManaged,
    }),
    [evidenceLocation, region, hasEvidencePro, isTeamMember, isManaged]
  );

  // Selection mode for tracking
  const selectionModeForTracking = useMemo(
    (): EvidenceSourceSelectionMode => sourceSelectionMode,
    [sourceSelectionMode]
  );

  // Preset selection for tracking
  const presetSelectionForTracking = useMemo(
    () => ({
      preset_primary_evidence: presetSelection.primaryEvidence,
      preset_local_guidelines: presetSelection.localGuidelines,
      preset_web_search: presetSelection.webSearch,
    }),
    [presetSelection]
  );

  // Track sidebar clicked
  const trackSidebarClicked = useCallback(() => {
    trackEvidenceSidebarClicked(baseProps);
  }, [baseProps]);

  // Track sources list clicked
  const trackSourcesListClicked = useCallback(() => {
    trackEvidenceSourcesListClicked(baseProps);
  }, [baseProps]);

  // Track preset clicked
  const trackPresetClicked = useCallback(
    (
      presetType: 'primary_evidence' | 'local_guidelines' | 'web_search',
      isEnabled: boolean
    ) => {
      trackEvidenceSourcesListPresetClicked({
        ...baseProps,
        preset_type: presetType,
        is_enabled: isEnabled,
      });
    },
    [baseProps]
  );

  // Track custom sources clicked
  const trackCustomSourcesClicked = useCallback(
    (customSourcesCount: number) => {
      trackEvidenceSourcesListCustomClicked({
        ...baseProps,
        custom_sources_count: customSourcesCount,
      });
    },
    [baseProps]
  );

  // Track local guidelines clicked
  const trackLocalGuidelinesClicked = useCallback(
    (isEnabled: boolean) => {
      trackEvidenceSourcesListLocalGuidelinesClicked({
        ...baseProps,
        is_enabled: isEnabled,
      });
    },
    [baseProps]
  );

  // Track web search clicked
  const trackWebSearchClicked = useCallback(
    (isEnabled: boolean) => {
      trackEvidenceSourcesListWebSearchClicked({
        ...baseProps,
        is_enabled: isEnabled,
      });
    },
    [baseProps]
  );

  // Track chat submitted
  const trackChatSubmitted = useCallback(
    (params: {
      query: string;
      sources?: string[];
      chatId?: string;
      sessionId?: string;
      entryMethod?: MessageEntryMethod;
      chipLabel?: string;
      chipCategory?: string;
      chipOrigin?: ChipOrigin;
      chipSurface?: ChipSurface;
      queued?: boolean;
      queueDurationMs?: number;
    }) => {
      trackEvidenceChatSubmitted({
        ...baseProps,
        evidence_location:
          evidenceLocation === EvidenceLocation.InSession && sessionStage === 'during'
            ? EvidenceLocation.InSessionCentral
            : evidenceLocation,
        query_length: toRequiredInteger(params.query.length),
        word_count: toInteger(params.query.trim().split(/\s+/).filter(Boolean).length),
        sources_count: toInteger(params.sources?.length),
        sources: toStringArray(params.sources),
        selection_mode: selectionModeForTracking,
        ...presetSelectionForTracking,
        chat_id: params.chatId,
        session_id: params.sessionId,
        entry_method: params.entryMethod,
        chip_label: params.chipLabel,
        chip_category: params.chipCategory,
        origin: toChipOrigin(params.chipOrigin),
        surface: params.chipSurface,
        queued: params.queued,
        session_stage: toSessionStage(
          evidenceLocation === EvidenceLocation.InSession ? sessionStage : undefined
        ),
        queue_duration_ms: normalizeQueueDurationMs(
          params.queued,
          params.queueDurationMs
        ),
      });
    },
    [
      baseProps,
      selectionModeForTracking,
      presetSelectionForTracking,
      evidenceLocation,
      sessionStage,
    ]
  );

  // Track chat completed
  const trackChatCompleted = useCallback(
    (params: {
      durationMs: number;
      timeToFirstTokenMs?: number;
      referencesCount: number;
      queryLength: number;
      responseLength?: number;
      responseWordCount?: number;
      chatId?: string;
      messageId?: string;
      sessionId?: string;
      tavilySearchCount?: number;
      routedMode?: string;
      toolsUsed?: string[];
      entryMethod?: string;
      chipLabel?: string;
      chipCategory?: string;
      chipOrigin?: ChipOrigin;
      chipSurface?: ChipSurface;
      queued?: boolean;
      queueDurationMs?: number;
      quotaExhaustedMessageShown?: boolean;
    }) => {
      trackEvidenceChatCompleted({
        ...baseProps,
        duration_ms: toRequiredInteger(params.durationMs),
        time_to_first_token_ms: toInteger(params.timeToFirstTokenMs),
        references_count: toRequiredInteger(params.referencesCount),
        query_length: toRequiredInteger(params.queryLength),
        response_length: toInteger(params.responseLength),
        word_count: toInteger(params.responseWordCount),
        chat_id: params.chatId,
        message_id: params.messageId,
        session_id: params.sessionId,
        tavily_search_count: toInteger(params.tavilySearchCount),
        routed_mode: params.routedMode as 'legacy' | 'evidence' | undefined,
        tools_used: toStringArray(params.toolsUsed),
        entry_method: params.entryMethod as MessageEntryMethod | undefined,
        chip_label: params.chipLabel,
        chip_category: params.chipCategory,
        origin: toChipOrigin(params.chipOrigin),
        surface: params.chipSurface,
        queued: params.queued,
        queue_duration_ms: normalizeQueueDurationMs(
          params.queued,
          params.queueDurationMs
        ),
        ...(params.quotaExhaustedMessageShown !== undefined && {
          quota_exhausted_message_shown: params.quotaExhaustedMessageShown,
        }),
      });
    },
    [baseProps]
  );

  // Track chat error
  const trackChatError = useCallback(
    (params: {
      errorMessage?: string;
      errorCode?: string;
      durationMs?: number;
      queryLength?: number;
      chatId?: string;
      sessionId?: string;
      routedMode?: string;
      errorType?: EvidenceChatErrorType;
      httpStatus?: number;
      blockType?: EvidenceChatBlockType;
      attachmentReason?: EvidenceChatAttachmentReason;
    }) => {
      trackEvidenceChatError({
        ...baseProps,
        error_message: params.errorMessage,
        error_code: params.errorCode,
        duration_ms: params.durationMs,
        query_length: params.queryLength,
        chat_id: params.chatId,
        session_id: params.sessionId,
        routed_mode: params.routedMode as 'legacy' | 'evidence' | undefined,
        error_type: params.errorType,
        http_status: params.httpStatus,
        block_type: params.blockType,
        attachment_reason: params.attachmentReason,
      });
    },
    [baseProps]
  );

  // Track chat aborted
  const trackChatAborted = useCallback(
    (params: {
      durationMs?: number;
      queryLength: number;
      chatId?: string;
      messageId?: string;
      sessionId?: string;
    }) => {
      trackEvidenceChatAborted({
        ...baseProps,
        duration_ms: params.durationMs,
        query_length: params.queryLength,
        chat_id: params.chatId,
        message_id: params.messageId,
        session_id: params.sessionId,
      });
    },
    [baseProps]
  );

  // Track pro trial button started
  const trackProTrialButtonStarted = useCallback(() => {
    trackEvidenceProTrialButtonStarted(baseProps);
  }, [baseProps]);

  // Track deep link sign in clicked
  const trackDeepLinkSignInClicked = useCallback(() => {
    trackEvidenceDeepLinkSignInClicked({
      evidence_location: evidenceLocation,
    });
  }, [evidenceLocation]);

  // Track deep link sign up clicked
  const trackDeepLinkSignUpClicked = useCallback(() => {
    trackEvidenceDeepLinkSignUpClicked({
      evidence_location: evidenceLocation,
    });
  }, [evidenceLocation]);

  // Track new chat clicked
  const trackNewChatClicked = useCallback(
    (clickSource: 'sidebar' | 'header' | 'history_overlay') => {
      trackEvidenceNewChatClicked({
        ...baseProps,
        click_source: clickSource,
      });
    },
    [baseProps]
  );

  // Track delete chat clicked
  const trackDeleteChatClicked = useCallback(
    (chatId: string) => {
      trackEvidenceDeleteChatClicked({
        ...baseProps,
        chat_id: chatId,
      });
    },
    [baseProps]
  );

  // Track save chat clicked
  const trackChatSaved = useCallback(
    (chatId: string, clickSource: 'sidebar' | 'header' | 'saved_section') => {
      trackEvidenceSaveChatClicked({
        ...baseProps,
        chat_id: chatId,
        click_source: clickSource,
      });
    },
    [baseProps]
  );

  // Track unsave chat clicked
  const trackChatUnsaved = useCallback(
    (chatId: string, clickSource: 'sidebar' | 'header' | 'saved_section') => {
      trackEvidenceUnsaveChatClicked({
        ...baseProps,
        chat_id: chatId,
        click_source: clickSource,
      });
    },
    [baseProps]
  );

  // Track past session clicked
  const trackPastSessionClicked = useCallback(
    (chatId: string) => {
      trackEvidenceClickPastSessionClicked({
        ...baseProps,
        chat_id: chatId,
      });
    },
    [baseProps]
  );

  // Track chat created (new chat with first message)
  const trackChatCreated = useCallback(
    (chatId: string, sessionId?: string) => {
      trackChatCreatedHost({
        source: 'evidence',
        ...baseProps,
        chat_id: chatId,
        location: evidenceLocation,
        session_id: sessionId,
      });
    },
    [baseProps, evidenceLocation]
  );

  // Track chat viewed (existing chat loaded)
  const trackChatViewed = useCallback(
    (chatId: string, sessionId?: string) => {
      trackChatViewedHost({
        source: 'evidence',
        ...baseProps,
        chat_id: chatId,
        location: evidenceLocation,
        session_id: sessionId,
      });
    },
    [baseProps, evidenceLocation]
  );

  // Track chat deleted (after successful deletion)
  const trackChatDeleted = useCallback(
    (chatId: string, sessionId?: string) => {
      trackChatDeletedHost({
        source: 'evidence',
        ...baseProps,
        chat_id: chatId,
        location: evidenceLocation,
        session_id: sessionId,
      });
    },
    [baseProps, evidenceLocation]
  );

  // Track sidebar open
  const trackSidebarOpen = useCallback(() => {
    trackEvidenceChatSidebarOpen(baseProps);
  }, [baseProps]);

  // Track sidebar close
  const trackSidebarClose = useCallback(() => {
    trackEvidenceChatSidebarClose(baseProps);
  }, [baseProps]);

  // Track toast shown
  const trackToastShown = useCallback(
    (chatId: string) => {
      trackEvidenceToastShown({
        ...baseProps,
        chat_id: chatId,
      });
    },
    [baseProps]
  );

  // Track toast view clicked
  const trackToastViewClicked = useCallback(
    (chatId: string) => {
      trackEvidenceToastViewClicked({
        ...baseProps,
        chat_id: chatId,
      });
    },
    [baseProps]
  );

  // Track language changed from evidence
  const trackLanguageChanged = useCallback(
    (languageCode: string) => {
      trackEvidenceLanguageChanged({
        ...baseProps,
        language_code: languageCode,
      });
    },
    [baseProps]
  );

  // Track specialty changed from evidence
  const trackSpecialtyChanged = useCallback(
    (specialtyId: string) => {
      trackEvidenceSpecialtyChanged({
        ...baseProps,
        specialty_id: specialtyId,
      });
    },
    [baseProps]
  );

  // Track jurisdiction changed from evidence
  const trackJurisdictionChanged = useCallback(
    (jurisdictionCode: string) => {
      trackEvidenceJurisdictionChanged({
        ...baseProps,
        jurisdiction_code: jurisdictionCode,
      });
    },
    [baseProps]
  );

  // Track share modal opened
  const trackShareModalOpened = useCallback(
    (chatId: string) => {
      track({
        event_name: TrackingEventName.EvidenceShareModalOpened,
        props: {
          ...baseProps,
          chat_id: chatId,
        },
        options: { amplitude: false },
      });
    },
    [baseProps]
  );

  // Track share link copied
  const trackShareLinkCopied = useCallback(
    (chatId: string) => {
      track({
        event_name: TrackingEventName.EvidenceShareLinkCopied,
        props: {
          ...baseProps,
          chat_id: chatId,
        },
        options: { amplitude: false },
      });
    },
    [baseProps]
  );

  // Track share social clicked
  const trackShareSocialClicked = useCallback(
    (chatId: string, platform: EvidenceShareSocialPlatform) => {
      track({
        event_name: TrackingEventName.EvidenceShareSocialClicked,
        props: {
          ...baseProps,
          chat_id: chatId,
          platform,
        },
        options: { amplitude: false },
      });
    },
    [baseProps]
  );

  // Track shared chat opened
  const trackSharedChatOpened = useCallback(
    (shareToken: string, isAuthenticated: boolean) => {
      track({
        event_name: TrackingEventName.EvidenceSharedChatOpened,
        props: {
          ...baseProps,
          share_token: shareToken,
          is_authenticated: isAuthenticated,
        },
        options: { amplitude: false },
      });
    },
    [baseProps]
  );

  // Track Evidence copy-to-clipboard PHI/PII confirmation popover lifecycle
  // (shown / confirmed / dismissed). Forms the audit trail required by SWR-88.
  const trackCopyConfirmation = useCallback(
    (
      action: EvidenceCopyConfirmationAction,
      surface: EvidenceCopyConfirmationSurface,
      chatId: string,
      messageId?: string | null
    ) => {
      track({
        event_name: TrackingEventName.EvidenceCopyConfirmation,
        props: {
          ...baseProps,
          action,
          surface,
          chat_id: chatId,
          message_id: messageId ?? undefined,
        },
        options: { amplitude: false },
      });
    },
    [baseProps]
  );

  // Track successful Evidence chat copy (EVI-2101). Emit only after clipboard write succeeds.
  // Companion adoption signal to note_insert_applied (add/replace/create-document).
  const trackCopied = useCallback(
    (params: {
      surface: EvidenceCopyConfirmationSurface;
      chatId?: string | null;
      messageId?: string | null;
      sessionId?: string | null;
      mode?: string;
    }) => {
      track({
        event_name: TrackingEventName.EvidenceCopied,
        props: {
          ...baseProps,
          surface: params.surface,
          chat_id: params.chatId || undefined,
          message_id: params.messageId || undefined,
          session_id: params.sessionId || undefined,
          mode: params.mode,
        },
        options: { amplitude: false },
      });
    },
    [baseProps]
  );

  // Track opening the "Check other sources" dropdown on a chat response. The
  // subsequent re-run submits with entry_method: 'check_other_sources'.
  const trackCheckOtherSourcesClicked = useCallback(
    (params: {
      chatId?: string | null;
      messageId?: string | null;
      sessionId?: string | null;
    }) => {
      track({
        event_name: TrackingEventName.EvidenceCheckOtherSourcesClicked,
        props: {
          ...baseProps,
          chat_id: params.chatId || undefined,
          message_id: params.messageId || undefined,
          session_id: params.sessionId || undefined,
        },
        options: { amplitude: false },
      });
    },
    [baseProps]
  );

  return {
    // Props
    evidenceLocation,
    region,
    isEvidencePro: hasEvidencePro,
    isTeamMember,
    baseProps,
    selectionModeForTracking,
    presetSelectionForTracking,

    // Track functions
    trackSidebarClicked,
    trackSourcesListClicked,
    trackPresetClicked,
    trackCustomSourcesClicked,
    trackLocalGuidelinesClicked,
    trackWebSearchClicked,
    trackChatSubmitted,
    trackChatCompleted,
    trackChatError,
    trackChatAborted,
    trackProTrialButtonStarted,
    trackDeepLinkSignInClicked,
    trackDeepLinkSignUpClicked,
    trackNewChatClicked,
    trackDeleteChatClicked,
    trackChatSaved,
    trackChatUnsaved,
    trackPastSessionClicked,
    trackChatCreated,
    trackChatViewed,
    trackChatDeleted,
    trackSidebarOpen,
    trackSidebarClose,
    trackToastShown,
    trackToastViewClicked,
    trackLanguageChanged,
    trackSpecialtyChanged,
    trackJurisdictionChanged,
    trackShareModalOpened,
    trackShareLinkCopied,
    trackShareSocialClicked,
    trackSharedChatOpened,
    trackCopyConfirmation,
    trackCopied,
    trackCheckOtherSourcesClicked,
  };
};

/** Tracks a block-level error exactly once per mount. */
export function useBlockErrorTracking(
  blockType: EvidenceChatBlockType,
  status: string,
  error?: string
) {
  const report = useBlockErrorReporter();
  useInvokeOnce(() => report(blockType, error), status === 'error');
}

export function useBlockErrorReporter() {
  const { trackChatError } = useEvidenceAnalytics();
  return useCallback(
    (blockType: EvidenceChatBlockType, error?: string) =>
      trackChatError({
        errorType: 'block',
        blockType,
        errorMessage: error || `${blockType} failed`,
      }),
    [trackChatError]
  );
}
